// apps/web/src/components/BadgeIcon/BadgeIcon.tsx
// ─────────────────────────────────────────────────────────────────────────
// Maps a BadgeIconKind → a simple, symbolic stroke glyph. Deliberately
// restrained line-art (no mascots, no cartoon medals) per the badge visual
// language. `currentColor` drives stroke so callers theme via CSS.
// ─────────────────────────────────────────────────────────────────────────

import React from 'react';
import type { BadgeIconKind } from '@nisk/core/badges';

interface Props {
	kind: BadgeIconKind;
	className?: string;
}

const PATHS: Record<BadgeIconKind, React.ReactNode> = {
	spark: (
		<path d='M12 2v6m0 8v6m10-10h-6M8 12H2m15.5-5.5L13 11m-2 2-4.5 4.5m11 0L13 13m-2-2L6.5 6.5' />
	),
	check: <path d='M4 12.5l5 5L20 6' />,
	door: <path d='M6 3h9v18H6zM15 3l3 2v14l-3 2M11 12h.5' />,
	step: <path d='M3 20h6v-5h6v-5h6V5' />,
	calculator: (
		<path d='M6 3h12v18H6zM9 7h6M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15v3' />
	),
	target: (
		<path d='M12 12m-9 0a9 9 0 1018 0 9 9 0 10-18 0M12 12m-4 0a4 4 0 108 0 4 4 0 10-8 0M12 12h.01' />
	),
	compass: (
		<path d='M12 12m-9 0a9 9 0 1018 0 9 9 0 10-18 0M16 8l-2.5 5.5L8 16l2.5-5.5z' />
	),
	graph: <path d='M3 3v18h18M7 14l3-4 3 3 5-7' />,
	brain: (
		<path d='M9 4a3 3 0 00-3 3 3 3 0 00-1 5.5A3 3 0 007 18a3 3 0 005 1 3 3 0 005-1 3 3 0 002-5.5A3 3 0 0018 7a3 3 0 00-3-3 3 3 0 00-3 1 3 3 0 00-3-1zM12 5v14' />
	),
	lock: <path d='M6 11h12v9H6zM8 11V7a4 4 0 018 0v4M12 15v2' />,
	book: <path d='M4 5a2 2 0 012-2h12v16H6a2 2 0 00-2 2zM18 19H6M9 7h6' />,
	medal: (
		<path d='M8 3l4 8 4-8M12 11v3m0 0a4 4 0 100 8 4 4 0 000-8m0 4h.01' />
	),
	flame: (
		<path d='M12 22c4 0 6-3 6-6 0-4-4-5-3-9-3 1-5 4-5 7 0-1.5-1-3-1-3-1 1.5-3 3-3 6 0 3 2 5 6 5z' />
	),
	calendar: <path d='M4 6h16v15H4zM4 10h16M8 3v4M16 3v4M9 15l2 2 4-4' />,
	chain: <path d='M9 12h6M8 8h-.5a4 4 0 000 8H8m8-8h.5a4 4 0 010 8H16' />,
	prism: <path d='M12 3L3 19h18zM12 3v16M12 19l-4.5-8M12 19l4.5-8' />,
	globe: (
		<path d='M12 12m-9 0a9 9 0 1018 0 9 9 0 10-18 0M3 12h18M12 3c2.5 2.5 3.5 6 3.5 9s-1 6.5-3.5 9c-2.5-2.5-3.5-6-3.5-9s1-6.5 3.5-9z' />
	),
	conversation: <path d='M4 5h12v9H8l-4 4zM10 18h8l2 2V9h-2' />,
	mountain: <path d='M3 20l6-12 4 7 2-3 6 8zM9 8l2 3' />,
	flag: <path d='M5 21V4m0 0h11l-2 4 2 4H5' />,
	path: <path d='M6 21c0-4 12-4 12-9s-8-4-8-8' />,
};

const BadgeIcon: React.FC<Props> = ({ kind, className }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={1.6}
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'>
		{PATHS[kind]}
	</svg>
);

export default BadgeIcon;
